import {
  CurecallButton,
  CurecallCopyButton,
  CurecallList,
  CurecallModalContainer,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  ECurecallSize,
  LoadingSpinner,
} from '@honestica/curecall-ui'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { InteropLogListComponent } from './interop-log-list.component'
import { InteropEntityTreeComponent } from './interop-entity-tree.component'
import { buildInteropEntityTree } from './interop-entities.util'
import { TInteropMapping } from './interop-mapping.type'
import { EInteropLogSortOrder, TPatientInteropLog } from './interop-log.type'
import { TPatient } from 'src/types/patient'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { PatientService } from 'src/services/patient.service'
import { ProjectService } from 'src/services/project.service'
import { useNotification } from 'src/providers/notification.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { twMerge } from 'src/utils/tw-merge.util'

type TPatientInteropLogsModalProps = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
  isOpen: boolean
  onToggle: VoidFunction
}

/**
 * Max interop log number deemed safe before overloading the DOM on a low-end
 * computer
 */
export const MAX_SAFE_INTEROP_LOGS_TO_DISPLAY = 1000

export const PatientInteropLogsModal = ({ patient, isOpen, onToggle }: TPatientInteropLogsModalProps) => {
  const { project } = useCurrentProject()
  const { t } = useTranslation()
  const { notify } = useNotification()
  const [interopLogsToDisplay, setInteropLogsToDisplay] = useState<TPatientInteropLog[]>([])
  const [sortOrder, setSortOrder] = useState<EInteropLogSortOrder>(EInteropLogSortOrder.DEFAULT)
  const [interopMappings, setInteropMappings] = useState<TInteropMapping[]>([])
  const [isEntityView, setIsEntityView] = useState<boolean>(true)
  const entityTree = useMemo(() => buildInteropEntityTree(interopLogsToDisplay), [interopLogsToDisplay])

  const copyAnonymizedHistory = async () => {
    try {
      const anonymizedInteropLogs = await PatientService.getPatientAnonymizedInteropLogsData(
        project.id,
        patient.id,
      )
      await navigator.clipboard.writeText(JSON.stringify(anonymizedInteropLogs, null, 2))
      notify({
        title: t('components.patient.interop-logs.copy-anonymized-history'),
        message: t('components.patient.interop-logs.copy-anonymized-history-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch (copyError) {
      notify({
        title: t('components.patient.interop-logs.copy-anonymized-history'),
        message: getErrorMessage(copyError),
        appearance: ECurecallNotificationAppearance.WARNING,
      })
    }
  }

  /**
   * ESC key handler to close the modal
   * Listens for the ESC key press and calls the onToggle function when pressed
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onToggle()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onToggle])

  const {
    isLoading,
    error,
    data: interopLogsData,
  } = useQuery({
    queryKey: ['patient-interop-logs', project.id, patient.id],
    queryFn: () => PatientService.getPatientInteropLogsData(project.id, patient.id),
  })

  const { data: interopMappingsData } = useQuery({
    queryKey: ['interop-mappings', project.id],
    queryFn: () => ProjectService.getInteropMappings(project.id),
  })

  useEffect(() => {
    if (interopLogsData) {
      const tableRowsData =
        Array.isArray(interopLogsData) && interopLogsData.length > 0 ? interopLogsData : []
      setInteropLogsToDisplay(tableRowsData)
    }
  }, [interopLogsData])

  useEffect(() => {
    if (interopMappingsData) {
      setInteropMappings(
        Array.isArray(interopMappingsData) && interopMappingsData.length > 0 ? interopMappingsData : [],
      )
    }
  }, [interopMappingsData])

  if (isLoading) {
    return (
      <CurecallModalContainer
        isOpen={isOpen}
        toggle={onToggle}
        dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
        title={t('components.patient.interop-logs.modal-title')}
      >
        <LoadingSpinner />
      </CurecallModalContainer>
    )
  }

  if (error || !interopLogsData) {
    return (
      <CurecallModalContainer
        isOpen={isOpen}
        toggle={onToggle}
        dialogClassName="sm:c-w-[750px] xl:c-w-[900px] [&>div]:c-flex [&>div]:c-flex-col"
        title={t('components.patient.interop-logs.modal-title')}
      >
        <p>{t('components.patient.interop-logs.error-loading-data')}</p>
      </CurecallModalContainer>
    )
  }

  if (interopLogsToDisplay.length > MAX_SAFE_INTEROP_LOGS_TO_DISPLAY) {
    // Sentry.captureMessage removed for POC - uncomment when Sentry is integrated
    console.warn('Too much Interop Logs to display on a single patient', {
      projectId: project.id,
      patientId: patient.id,
    })
  }

  const sortInteropLogs = (interopLogs: TPatientInteropLog[]) => {
    const dateOf: Record<EInteropLogSortOrder, ((interopLog: TPatientInteropLog) => number) | null> = {
      [EInteropLogSortOrder.DEFAULT]: null,
      [EInteropLogSortOrder.RECEPTION]: (interopLog) => new Date(interopLog.createdAt).getTime(),
      [EInteropLogSortOrder.EMISSION]: (interopLog) =>
        new Date(interopLog.payload?.timestamp ?? interopLog.createdAt).getTime(),
    }
    const getDate = dateOf[sortOrder]
    if (!getDate) {
      return interopLogs
    }
    return interopLogs.slice().sort((logA, logB) => getDate(logB) - getDate(logA))
  }

  const logsToDisplay = sortInteropLogs(interopLogsToDisplay)

  const sortOptions: { value: EInteropLogSortOrder; label: string }[] = [
    { value: EInteropLogSortOrder.DEFAULT, label: t('components.patient.interop-logs.sort-default') },
    { value: EInteropLogSortOrder.RECEPTION, label: t('components.patient.interop-logs.sort-reception') },
    { value: EInteropLogSortOrder.EMISSION, label: t('components.patient.interop-logs.sort-emission') },
  ]

  return (
    <CurecallModalContainer
      isOpen={isOpen}
      toggle={onToggle}
      dialogClassName="c-w-[90%] [&>div]:c-flex [&>div]:c-gap-2"
      title={
        <div className="c-flex c-items-baseline">
          <span>{t('components.patient.interop-logs.modal-title')}</span>
          <div className="c-flex c-items-center c-gap-1 c-ml-4 c-text-sm" role="group">
            {[true, false].map((isEntities) => (
              <button
                key={String(isEntities)}
                type="button"
                aria-pressed={isEntityView === isEntities}
                onClick={() => setIsEntityView(isEntities)}
                className={twMerge(
                  'c-rounded-full c-border c-px-2 c-py-1',
                  isEntityView === isEntities ? 'c-bg-blue-500 c-text-white' : 'c-bg-white',
                )}
              >
                {isEntities
                  ? t('components.patient.interop-logs.entities.view-entities')
                  : t('components.patient.interop-logs.entities.view-logs')}
              </button>
            ))}
          </div>
          <div className="c-grow c-flex c-items-center c-justify-end c-gap-4">
            {!isEntityView && (
              <div className="c-flex c-items-center c-gap-1 c-text-sm" role="group">
                {t('components.patient.interop-logs.sort-label')}
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={sortOrder === option.value}
                    onClick={() => setSortOrder(option.value)}
                    className={twMerge(
                      'c-rounded-full c-border c-px-2 c-py-1',
                      sortOrder === option.value ? 'c-bg-blue-500 c-text-white' : 'c-bg-white',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            <CurecallButton
              appearance={ECurecallButtonAppearance.SECONDARY}
              size={ECurecallSize.SMALL}
              onClick={copyAnonymizedHistory}
            >
              {t('components.patient.interop-logs.copy-anonymized-history')}
            </CurecallButton>
          </div>
        </div>
      }
    >
      <div className="c-flex-none c-w-2/3">
        {isEntityView ? (
          <InteropEntityTreeComponent tree={entityTree} patient={patient} project={project} />
        ) : logsToDisplay.length > 0 ? (
          <div>
            {logsToDisplay.length > MAX_SAFE_INTEROP_LOGS_TO_DISPLAY && (
              <p>
                {t('components.patient.interop-logs.max-number-reached', {
                  max: MAX_SAFE_INTEROP_LOGS_TO_DISPLAY,
                })}
              </p>
            )}
            <InteropLogListComponent
              patientInteropLogs={logsToDisplay}
              patient={patient}
              project={project}
              sortOrder={sortOrder}
            />
          </div>
        ) : (
          <p>{t('components.patient.interop-logs.no-data')}</p>
        )}
      </div>
      <div className="c-flex-1">
        {interopMappings.length > 0 && (
          <>
            {t('components.patient.interop-logs.mappings-title')}
            <CurecallList
              headersData={[
                { key: 'id', label: 'ID' },
                { key: 'description', label: 'Description' },
                { key: 'condition', label: 'Condition' },
              ]}
              rowsData={interopMappings.map((im) => ({
                id: im.id,
                description: im.description,
                condition: (
                  <CurecallCopyButton
                    ariaLabelText={t('components.patient.interop-logs.copy-mapping-condition')}
                    data={im.condition}
                    size={ECurecallSize.SMALL}
                  />
                ),
              }))}
            />
          </>
        )}
      </div>
    </CurecallModalContainer>
  )
}
